import React, { useEffect, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { PinDrop } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import MapStyle from '../../../components/MapStyle';

interface WizardMapPreviewProps {
  lat: number | null;
  lng: number | null;
}

const PLACEHOLDER_MESSAGE = 'Map preview will appear as you enter an address';

const MAP_HEIGHT = 240;
const MAP_CONTAINER_STYLE = { width: '100%', height: `${MAP_HEIGHT}px` };

/**
 * Map preview card for Step 1 of the Create/Edit Location wizard.
 *
 * - No coordinates → centered pin icon + helper message (default state).
 * - Coordinates present → embedded, non-interactive Google map with a marker.
 */
const WizardMapPreview = (props: WizardMapPreviewProps) => {
  const { lat, lng } = props;
  const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);

  const [center, setCenter] = useState<google.maps.LatLng | null>(null);

  useEffect(() => {
    const google = window.google;
    if (hasCoordinates && google?.maps) {
      setCenter(new google.maps.LatLng(lat as number, lng as number));
    }
  }, [lat, lng, hasCoordinates]);

  if (!hasCoordinates || !center) {
    return (
      <Box
        aria-label={PLACEHOLDER_MESSAGE}
        sx={{
          height: MAP_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'grey.50',
          color: 'text.secondary',
        }}
      >
        <PinDrop fontSize="large" />
        <Typography variant="body2">{PLACEHOLDER_MESSAGE}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
      <GoogleMap
        center={center}
        zoom={14}
        mapContainerStyle={MAP_CONTAINER_STYLE}
        options={{
          styles: MapStyle,
          mapTypeControl: false,
          disableDefaultUI: true,
          draggable: false,
          disableDoubleClickZoom: true,
          zoomControl: false,
        }}
      >
        <Marker position={center} />
      </GoogleMap>
    </Box>
  );
};

export default WizardMapPreview;

